import {attemptSessionId} from "./activity-continuity";
import {SessionActivityPanel} from "./SessionActivityPanel";
import {sessionAttendance} from "./session-attendance";
import { CorrectionsView, StudentsView, CompetenciesView, ReferenceMaintenancePanel } from "./TeacherSpaces";
import {sessionActivityOptions} from "./session-activity-options";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BarChart3,
  BookOpen,
  Cable,
  Check,
  ChevronDown,
  Cloud,
  CloudOff,
  GripVertical,
  History,
  KeyRound,
  LayoutGrid,
  ListChecks,
  LogOut,
  Menu,
  Play,
  Plus,
  Redo2,
  Save,
  Settings2,
  ShieldCheck,
  Square,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { AdminTeachersView, StudentAccountsView } from "./AccountViews";
import type { AuthUser, SyncReport } from "./AuthApp";
import { ActivityPicker } from "./ActivityPicker";
import { AccountHub, SettingsHub } from "./NavigationHubs";
import { CatalogView } from "./ManagementViews";
const WrittenHomeworks=lazy(()=>import("./WrittenHomeworks"));
const ClassBoard=lazy(()=>import("./ClassBoard"));
const ActivityLibraryView=lazy(()=>import("./ActivityLibraryView"));
import { ClassesView, RoomDefinition, SchoolClass, StudentSeed } from "./ClassesView";
import { HistorySession, SessionsView } from "./SessionsView";
import { StudentDashboard } from "./StudentDashboard";
import { PronoteView } from "./PronoteView";
import { useActivityLibrary } from "./activity-library-client";
import { sessionAccess, attemptMemberIds, type DigitalSession } from "./activity-library";
import { normalizeActivity } from "./normalized-activity";
import {
  ActivityDefinition,
  CatalogCompetency,
  SchoolLevel,
  defaultTeacherLevels,
  initialActivities,
  initialCatalog,
  orderCatalogForLevel,
} from "./catalog";

type Level = "A" | "B" | "C" | "D";
type CheckpointLevel = Level | "NE" | "ABS";
type StudentStatus = "todo" | "progress" | "done";
type Sex = "homme" | "femme";
type ContainerId = string;
type AppView = "board" | "written" | "corrections" | "competencies" | "rooms" | "maintenance" | "live" | "classes" | "activities" | "history" | "catalog" | "student" | "accounts" | "admin" | "pronote" | "settings" | "account";
type Cohort = "all" | "A" | "B";
type SyncStatus = "synced" | "pending" | "syncing" | "offline" | "error";
type Period = 1 | 2 | 3;

type WorkGroup = {
  id: string;
  name: string;
};

type SessionRecord = {
  digitalSessionId?: string;
  id: string;
  classId: string;
  roomId: string;
  cohort: Cohort;
  activityId: string;
  startedAt: string;
  period?: Period;
  endedAt?: string;
  participation?: Record<string, Level>;
  absentIds?: string[];
  evaluations?: Record<string, Record<string, CheckpointLevel>>;
  attendanceAuto?: Record<string, Record<string, {previous?: CheckpointLevel; previousAt?: string}>>;
};

type Student = {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  hue: string;
  sex?: Sex;
  photoUrl?: string;
  participation: Level;
  absent: boolean;
  results: Record<string, CheckpointLevel>;
  resultUpdatedAt?: Record<string, string>;
  officialGroup?: "A" | "B";
};

const initialStudents: Student[] = [
  ["s1", "Élève", "Démo 01", "E1", "#1565c0", "homme"],
  ["s2", "Élève", "Démo 02", "E2", "#8e3a75", "femme"],
  ["s3", "Élève", "Démo 03", "E3", "#3d6f45", "homme"],
  ["s4", "Élève", "Démo 04", "E4", "#b45a2a", "femme"],
  ["s5", "Élève", "Démo 05", "E5", "#365c9a", "homme"],
  ["s6", "Élève", "Démo 06", "E6", "#7c4a9e", "femme"],
  ["s7", "Élève", "Démo 07", "E7", "#9b3d43", "homme"],
  ["s8", "Élève", "Démo 08", "E8", "#32746d", "femme"],
  ["s9", "Élève", "Démo 09", "E9", "#7d5b2b", "homme"],
  ["s10", "Élève", "Démo 10", "E10", "#5b4ba3", "femme"],
  ["s11", "Élève", "Démo 11", "E11", "#2d718b", "homme"],
  ["s12", "Élève", "Démo 12", "E12", "#a04466", "femme"],
].map(([id, firstName, lastName, initials, hue, sex]) => ({
  id,
  firstName,
  lastName,
  initials,
  hue,
  sex: sex as Sex,
  participation: "B" as Level,
  absent: false,
  results: {},
}));

const demoClass: SchoolClass = {
  id: "terminale-2-demo",
  name: "Terminale 2",
  schoolLevel: "Tle Spé",
  subject: "Physique-chimie",
  students: initialStudents.map(({ id, firstName, lastName, sex }) => ({ id, firstName, lastName, sex: sex ?? "homme" })),
};

const studentColors = ["#1565c0", "#8e3a75", "#3d6f45", "#b45a2a", "#365c9a", "#7c4a9e", "#9b3d43", "#32746d"];

function studentFromSeed(seed: StudentSeed, index: number): Student {
  return {
    ...seed,
    initials: `${seed.firstName.charAt(0)}${seed.lastName.charAt(0)}`.toUpperCase(),
    hue: studentColors[index % studentColors.length],
    participation: "B",
    absent: false,
    results: {},
  };
}

function studentsForCohort(schoolClass: SchoolClass, cohort: Cohort) {
  return schoolClass.students.filter((student) => cohort === "all" || student.officialGroup === cohort);
}

function freshPlan(schoolClass: SchoolClass, cohort: Cohort) {
  const studentIds = studentsForCohort(schoolClass, cohort).map((student) => student.id);
  return {
    groups: [] as WorkGroup[],
    locations: { room: normalizeRoomSlots(studentIds, 40) } as Record<ContainerId, string[]>,
  };
}

function reconcilePlan(schoolClass: SchoolClass, cohort: Cohort, plan: { locations?: Record<string, string[]>; groups?: WorkGroup[] }) {
  const validStudentIds = new Set(studentsForCohort(schoolClass, cohort).map((student) => student.id));
  const groups = plan.groups ?? [];
  const validGroupIds = new Set(groups.map((group) => group.id));
  const locations: Record<string, string[]> = {};
  groups.forEach((group) => {
    locations[group.id] = (plan.locations?.[group.id] ?? []).filter((id) => validStudentIds.has(id));
  });
  const room = normalizeRoomSlots(plan.locations?.room ?? [], 40).map((item) => {
    if (item.startsWith("space-")) return item;
    if (item.startsWith("group:")) return validGroupIds.has(item.slice("group:".length)) ? item : newSpaceId();
    return validStudentIds.has(item) ? item : newSpaceId();
  });
  const placed = new Set([
    ...room.filter((item) => validStudentIds.has(item)),
    ...groups.flatMap((group) => locations[group.id]),
  ]);
  [...validStudentIds].filter((id) => !placed.has(id)).forEach((id) => {
    const emptyIndex = room.findIndex((item) => item.startsWith("space-"));
    if (emptyIndex >= 0) room[emptyIndex] = id;
    else room.push(id);
  });
  return { groups, locations: { ...locations, room } };
}

const defaultRooms: RoomDefinition[] = [{ id: "labo-3", name: "Labo 3" }];
const planStorageKey = (classId: string, roomId: string, cohort: Cohort) => `competenceo-plan-v1:${classId}:${roomId}:${cohort}`;

function storedActivePlan() {
  const classId = localStorage.getItem("competenceo-active-class-v1") ?? demoClass.id;
  const roomId = localStorage.getItem("competenceo-active-room-v1") ?? defaultRooms[0].id;
  const cohort = (localStorage.getItem("competenceo-active-cohort-v1") as Cohort | null) ?? "all";
  const saved = localStorage.getItem(planStorageKey(classId, roomId, cohort));
  return saved ? JSON.parse(saved) : null;
}

const newSpaceId = () => `space-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const resultKey = (activityId: string, competencyId: string) => `${activityId}:${competencyId}`;

function normalizeRoomSlots(items: string[], count: number) {
  const normalized = items.slice(0, count);
  const overflowItems = items.slice(count).filter((item) => !item.startsWith("space-"));
  overflowItems.forEach((item) => {
    const emptyIndex = normalized.findIndex((candidate) => candidate.startsWith("space-"));
    if (emptyIndex >= 0) normalized[emptyIndex] = item;
    else normalized.push(item);
  });
  while (normalized.length < count) normalized.push(newSpaceId());
  return normalized;
}

function statusFor(student: Student, activityId: string, competencyIds: string[]): StudentStatus {
  if (!activityId || competencyIds.length === 0) return "todo";
  const evaluated = competencyIds.filter((id) => student.results[resultKey(activityId, id)]);
  if (evaluated.length === 0) return "todo";
  if (evaluated.length === competencyIds.length) return "done";
  return "progress";
}

function avatarFor(student: Student) {
  if (student.photoUrl) return student.photoUrl;
  const sex: Sex = student.sex ?? (Number(student.id.replace(/\D/g, "")) % 2 === 0 ? "femme" : "homme");
  const hash = [...student.id].reduce((total, character) => total + character.charCodeAt(0), 0);
  const number = String((hash % 6) + 1).padStart(2, "0");
  return `/avatars/${sex}/${sex}-${number}.webp`;
}

function SortableStudentCard({
  student,
  activityId,
  competencyIds,
  selected,
  inGroup,
  editing,
  sessionActive,
  onInsertSpace,
  onSelect,
  onCycleParticipation,
}: {
  student: Student;
  activityId: string;
  competencyIds: string[];
  selected: boolean;
  inGroup?: boolean;
  editing?: boolean;
  sessionActive: boolean;
  onInsertSpace?: () => void;
  onSelect: () => void;
  onCycleParticipation: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: student.id,
    data: { type: "student" },
  });
  const status = statusFor(student, activityId, competencyIds);
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`student-card status-${status} ${inGroup ? "in-group" : ""} ${selected ? "selected" : ""} ${student.absent ? "absent" : ""} ${isDragging ? "dragging" : ""}`}
      onClick={onSelect}
      aria-label={`${student.firstName} ${student.lastName}`}
    >
      <div className="student-status-band">
        <span>
          {student.absent ? "Absent" : status === "todo" ? "À évaluer" : status === "progress" ? "En cours" : "Évalué"}
        </span>
        <button className="drag-handle" {...attributes} {...listeners} aria-label="Déplacer l'élève">
          <GripVertical size={16} />
        </button>
      </div>
      {inGroup && (
        <button className="drag-handle student-group-drag" {...attributes} {...listeners} aria-label="Sortir ou déplacer l'élève du sous-groupe">
          <GripVertical size={15} />
        </button>
      )}
      <div className="student-card-body">
        <div className={`avatar-wrap ${student.photoUrl ? "has-custom-photo" : ""}`} style={student.photoUrl ? { backgroundImage: `url("${student.photoUrl}")` } : undefined}>
          <img className={`avatar ${student.photoUrl ? "custom-photo" : ""}`} src={avatarFor(student)} alt={`Portrait de ${student.firstName} ${student.lastName}`} draggable={false} />
          <button
            className={`participation participation-${student.absent ? "absent" : student.participation.toLowerCase()}`}
            disabled={!sessionActive}
            onClick={(event) => {
              event.stopPropagation();
              onCycleParticipation();
            }}
            title="Modifier la participation"
            aria-label={`Participation : ${student.absent ? "absent" : student.participation}`}
          >
            {student.absent ? "ABS" : student.participation}
          </button>
        </div>
        <div className="student-name">
          <strong>{student.lastName}</strong>
          <span>{student.firstName}</span>
        </div>
      </div>
      {editing && onInsertSpace && (
        <button
          className="insert-space"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onInsertSpace();
          }}
          title="Insérer un espace après cet élève"
          aria-label="Insérer un espace après cet élève"
        >
          <Plus size={14} />
        </button>
      )}
    </article>
  );
}

function LabGroup({
  id,
  markerId,
  name,
  shortName,
  memberCount,
  status,
  children,
  selected,
  editing,
  onSelect,
  onDelete,
}: {
  id: string;
  markerId: string;
  name: string;
  shortName: string;
  memberCount: number;
  status: StudentStatus;
  children: React.ReactNode;
  selected: boolean;
  editing: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const droppable = useDroppable({ id, data: { type: "container" } });
  const sortable = useSortable({ id: markerId, data: { type: "group-marker" } });
  const columns = memberCount <= 3
    ? Math.max(memberCount, 1)
    : Math.min(Math.ceil(Math.sqrt(memberCount)), 8);
  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        "--group-span": columns,
        "--group-columns": columns,
      } as React.CSSProperties}
      className={`group-slot ${sortable.isDragging ? "dragging" : ""}`}
    >
      <section
        ref={droppable.setNodeRef}
        className={`lab-group status-${status} ${selected ? "selected" : ""} ${droppable.isOver ? "drop-active" : ""}`}
      >
        <div className="group-status-band">
          <button
            className="group-compact-select"
            onClick={onSelect}
            aria-label={`${name}, ${memberCount} élève${memberCount > 1 ? "s" : ""}`}
          >
            <span>{status === "todo" ? "À évaluer" : status === "progress" ? "En cours" : "Évalué"}</span>
            <strong>{shortName}</strong>
          </button>
          <div className="group-band-actions">
            <button className="group-drag" {...sortable.attributes} {...sortable.listeners} aria-label="Déplacer le sous-groupe">
              <GripVertical size={17} />
            </button>
          </div>
        </div>
        <div className="group-grid">{children}</div>
        {editing && (
          <button className="delete-group" onClick={onDelete}>
            <Trash2 size={15} /> Supprimer le sous-groupe
          </button>
        )}
      </section>
    </div>
  );
}

function SortableSpace({ id }: { id: string }) {
  const { setNodeRef, isOver } = useSortable({
    id,
    data: { type: "space" },
  });
  return (
    <div
      ref={setNodeRef}
      className={`empty-space ${isOver ? "drop-active" : ""}`}
      aria-label="Place vide"
    />
  );
}

function App({ currentUser, onLogout, onSync, onExport, onRestore }: { currentUser: AuthUser; onLogout: () => void; onSync: () => Promise<SyncReport | void>; onExport: () => Record<string, string>; onRestore: (data: Record<string, string>) => Promise<void> }) {
  const [students, setStudents] = useState<Student[]>(() => {
    const savedClassId = localStorage.getItem("competenceo-active-class-v1") ?? demoClass.id;
    const saved = localStorage.getItem(`competenceo-students-v1:${savedClassId}`) ?? localStorage.getItem("competenceo-demo-students");
    return saved ? JSON.parse(saved) : initialStudents;
  });
  const [groups, setGroups] = useState<WorkGroup[]>(() => {
    const activePlan = storedActivePlan();
    if (activePlan?.groups) return activePlan.groups;
    const saved = localStorage.getItem("competenceo-demo-groups");
    return saved ? JSON.parse(saved) : [{ id: "lab-1", name: "Groupe TP 1" }];
  });
  const [locations, setLocations] = useState<Record<ContainerId, string[]>>(() => {
    const activePlan = storedActivePlan();
    if (activePlan?.locations) return { ...activePlan.locations, room: normalizeRoomSlots(activePlan.locations.room ?? [], 40) };
    const saved = localStorage.getItem("competenceo-demo-plan");
    return saved
      ? JSON.parse(saved)
      : { room: normalizeRoomSlots([...initialStudents.slice(0, 8).map((s) => s.id), "group:lab-1"], 40), "lab-1": initialStudents.slice(8).map((s) => s.id) };
  });
  const [catalog, setCatalog] = useState<CatalogCompetency[]>(() => {
    const saved = localStorage.getItem("competenceo-catalog-v3");
    return saved ? JSON.parse(saved) : initialCatalog;
  });
  const [activities, setActivities] = useState<ActivityDefinition[]>(() => {
    const saved = localStorage.getItem("competenceo-activities-v3");
    return saved ? JSON.parse(saved) : initialActivities;
  });
  const [teacherLevels, setTeacherLevels] = useState<SchoolLevel[]>(() => {
    const saved = localStorage.getItem("competenceo-teacher-levels-v1");
    return saved ? JSON.parse(saved) : defaultTeacherLevels;
  });
  const [classes, setClasses] = useState<SchoolClass[]>(() => {
    const saved = localStorage.getItem("competenceo-classes-v1");
    return saved ? JSON.parse(saved) : [demoClass];
  });
  const [activeClassId, setActiveClassId] = useState(() => localStorage.getItem("competenceo-active-class-v1") ?? demoClass.id);
  const [rooms, setRooms] = useState<RoomDefinition[]>(() => {
    const saved = localStorage.getItem("competenceo-rooms-v1");
    return saved ? JSON.parse(saved) : defaultRooms;
  });
  const [activeRoomId, setActiveRoomId] = useState(() => localStorage.getItem("competenceo-active-room-v1") ?? defaultRooms[0].id);
  const [activeCohort, setActiveCohort] = useState<Cohort>(() => (localStorage.getItem("competenceo-active-cohort-v1") as Cohort | null) ?? "all");
  const [activePeriod, setActivePeriod] = useState<Period>(() => {
    const saved = Number(localStorage.getItem("competenceo-active-period-v1"));
    return saved === 2 || saved === 3 ? saved : 1;
  });
  const [selected, setSelected] = useState<{ type: "student" | "group"; id: string }>({ type: "student", id: "s1" });
  const [activityId, setActivityId] = useState(() => localStorage.getItem("competenceo-current-activity") ?? "dosage-etalonnage");
  const [currentSession, setCurrentSession] = useState<SessionRecord | null>(() => {
    const saved = localStorage.getItem("competenceo-current-session-v1");
    return saved ? JSON.parse(saved) : null;
  });
  const [sessions, setSessions] = useState<SessionRecord[]>(() => {
    const saved = localStorage.getItem("competenceo-sessions-v1");
    return saved ? JSON.parse(saved) : [];
  });
  const [view, setView] = useState<AppView>(() => location.hash.startsWith('#board=')||new URLSearchParams(location.search).get('view')==='board'?'board':new URLSearchParams(location.search).get("view")==="written"?"written":window.location.pathname === "/activity-library" ? "activities" : new URLSearchParams(window.location.search).get("view") === "corrections" ? "corrections" : new URLSearchParams(window.location.search).get("view") === "settings" ? "settings" : "live");
  const [correctionTarget,setCorrectionTarget]=useState({activityId:new URLSearchParams(location.search).get("activityId")??"",attemptId:"",consultation:false,key:0});
  const [profileTarget,setProfileTarget]=useState<{classId:string;studentId:string}|undefined>();
  const [tracking,setTracking]=useState(false);
  const attendanceSaving = useRef(false);
  const [libraryVisited,setLibraryVisited]=useState(view==="activities");
  useEffect(()=>{if(view==="activities")setLibraryVisited(true);},[view]);
  const openWorks=(activityId="",attemptId="",consultation=false)=>{setCorrectionTarget(t=>({activityId,attemptId,consultation,key:t.key+1}));setTracking(false);setView("corrections");};
  const digitalLibrary = useActivityLibrary();
  useEffect(() => {
    const deleted = new Set(digitalLibrary.data?.state.deletedActivities?.map(a=>a.id));
    if (!deleted.size) return;
    setActivities(rows=>rows.some(a=>deleted.has(a.id))?rows.filter(a=>!deleted.has(a.id)):rows);
    const cleanSession = (session:SessionRecord) => {
      if (deleted.has(session.activityId)) return null;
      const evaluations = Object.fromEntries(Object.entries(session.evaluations??{}).map(([student,results])=>[
        student,Object.fromEntries(Object.entries(results).filter(([key])=>!deleted.has(key.slice(0,key.lastIndexOf(':')))))
      ]));
      return JSON.stringify(evaluations)===JSON.stringify(session.evaluations??{})?session:{...session,evaluations};
    };
    setSessions(rows=>{const next=rows.flatMap(s=>{const clean=cleanSession(s);return clean?[clean]:[];});return next.length===rows.length&&next.every((s,i)=>s===rows[i])?rows:next;});
    setCurrentSession(session=>session?cleanSession(session):null);
    setActivityId(id=>deleted.has(id)?'':id);
  },[digitalLibrary.data?.state.deletedActivities]);
  const [launchError, setLaunchError] = useState("");
  const [sessionClock, setSessionClock] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setSessionClock(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [evaluationOpen, setEvaluationOpen] = useState(false);
  const [dashboardStudentId, setDashboardStudentId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(navigator.onLine ? "pending" : "offline");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 7 } }));

  useEffect(() => {
    localStorage.setItem("competenceo-demo-students", JSON.stringify(students));
    localStorage.setItem("competenceo-demo-plan", JSON.stringify(locations));
    localStorage.setItem("competenceo-demo-groups", JSON.stringify(groups));
    localStorage.setItem(`competenceo-students-v1:${activeClassId}`, JSON.stringify(students));
    localStorage.setItem(planStorageKey(activeClassId, activeRoomId, activeCohort), JSON.stringify({ locations, groups }));
  }, [students, locations, groups, activeClassId, activeRoomId, activeCohort]);

  useEffect(() => {
    localStorage.setItem("competenceo-classes-v1", JSON.stringify(classes));
    localStorage.setItem("competenceo-active-class-v1", activeClassId);
    localStorage.setItem("competenceo-rooms-v1", JSON.stringify(rooms));
    localStorage.setItem("competenceo-active-room-v1", activeRoomId);
    localStorage.setItem("competenceo-active-cohort-v1", activeCohort);
    localStorage.setItem("competenceo-active-period-v1", String(activePeriod));
  }, [classes, activeClassId, rooms, activeRoomId, activeCohort, activePeriod]);

  useEffect(() => {
    localStorage.setItem("competenceo-catalog-v3", JSON.stringify(catalog));
    localStorage.setItem("competenceo-activities-v3", JSON.stringify(activities));
    localStorage.setItem("competenceo-current-activity", activityId);
  }, [catalog, activities, activityId]);

  useEffect(() => {
    localStorage.setItem("competenceo-teacher-levels-v1", JSON.stringify(teacherLevels));
  }, [teacherLevels]);

  useEffect(() => {
    localStorage.setItem("competenceo-sessions-v1", JSON.stringify(sessions));
    if (currentSession) localStorage.setItem("competenceo-current-session-v1", JSON.stringify(currentSession));
    else localStorage.removeItem("competenceo-current-session-v1");
  }, [sessions, currentSession]);

  function persistSharedState() {
    localStorage.setItem(`competenceo-students-v1:${activeClassId}`, JSON.stringify(students));
    localStorage.setItem(planStorageKey(activeClassId, activeRoomId, activeCohort), JSON.stringify({ locations, groups }));
    localStorage.setItem("competenceo-classes-v1", JSON.stringify(classes));
    localStorage.setItem("competenceo-rooms-v1", JSON.stringify(rooms));
    localStorage.setItem("competenceo-catalog-v3", JSON.stringify(catalog));
    localStorage.setItem("competenceo-activities-v3", JSON.stringify(activities));
    localStorage.setItem("competenceo-teacher-levels-v1", JSON.stringify(teacherLevels));
    localStorage.setItem("competenceo-sessions-v1", JSON.stringify(sessions));
    if (currentSession) localStorage.setItem("competenceo-current-session-v1", JSON.stringify(currentSession));
    else localStorage.removeItem("competenceo-current-session-v1");
  }

  async function synchronizeNow(showReport = false) {
    if (!navigator.onLine) { setSyncStatus("offline"); return; }
    persistSharedState();
    setSyncStatus("syncing");
    try {
      const report = await onSync();
      setSyncStatus("synced");
      if (showReport && report) {
        window.alert(`Serveur confirmé\nCompte : ${currentUser.email ?? currentUser.fullName}\nClasses : ${report.classCount}\nSalles : ${report.roomCount}\nRévision : ${report.revision}`);
      }
    }
    catch (reason) {
      setSyncStatus("error");
      if (showReport) window.alert(reason instanceof Error ? `Synchronisation impossible : ${reason.message}` : "Synchronisation impossible.");
    }
  }

  useEffect(() => {
    setSyncStatus(online ? "pending" : "offline");
    if (!online) return;
    const timer = window.setTimeout(() => {
      void synchronizeNow();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [students, locations, groups, catalog, activities, teacherLevels, classes, activeClassId, rooms, activeRoomId, activeCohort, activePeriod, sessions, currentSession, onSync, online]);

  useEffect(() => {
    setLocations((current) => ({ ...current, room: normalizeRoomSlots(current.room, 40) }));
  }, []);

  useEffect(() => {
    setLocations((current) => {
      const missingMarkers = groups
        .map((group) => `group:${group.id}`)
        .filter((marker) => !current.room.includes(marker));
      if (!missingMarkers.length) return current;
      const room = [...current.room];
      missingMarkers.forEach((marker) => {
        const emptyIndex = room.findIndex((item) => item.startsWith("space-"));
        if (emptyIndex >= 0) room[emptyIndex] = marker;
      });
      return { ...current, room };
    });
  }, [groups]);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const studentById = useMemo(() => Object.fromEntries(students.map((student) => [student.id, student])), [students]);
  const activeClass = classes.find((schoolClass) => schoolClass.id === activeClassId) ?? classes[0] ?? demoClass;
  const availableRooms = rooms.filter((room) => !activeClass.roomIds || activeClass.roomIds.includes(room.id));
  const activeRoom = availableRooms.find((room) => room.id === activeRoomId) ?? availableRooms[0] ?? rooms[0] ?? defaultRooms[0];
  const groupACount = activeClass.students.filter((student) => student.officialGroup === "A").length;
  const groupBCount = activeClass.students.filter((student) => student.officialGroup === "B").length;
  const hasOfficialGroups = groupACount > 0 || groupBCount > 0;
  const selectedStudent = selected.type === "student" ? studentById[selected.id] : null;
  const dashboardStudent = dashboardStudentId ? studentById[dashboardStudentId] : null;
  const selectedGroup = selected.type === "group" ? groups.find((group) => group.id === selected.id) : null;
  const selectedIds = selected.type === "group"
    ? (selectedGroup ? locations[selected.id] ?? [] : []).filter((id) => Boolean(studentById[id]))
    : selectedStudent ? [selectedStudent.id] : [];
  const availableSessionActivities = sessionActivityOptions(activities, digitalLibrary.data?.state, activeClass.id, activeClass.schoolLevel, currentSession?.activityId);
  const selectedActivity = availableSessionActivities.find((item) => item.id === activityId && item.schoolLevel === activeClass.schoolLevel) ?? null;
  const libraryActivity = digitalLibrary.data?.state.activities.find(a => a.id === selectedActivity?.id);
  const libraryVersion = digitalLibrary.data?.state.versions.find(v => v.id === libraryActivity?.id && v.version === libraryActivity.version);
  const digitalSession = digitalLibrary.data?.state.digitalSessions?.find(d => currentSession?.digitalSessionId
    ? d.id === currentSession.digitalSessionId : d.classId === activeClass.id && d.activityId === selectedActivity?.id && d.access !== "ended" &&
      digitalLibrary.data?.state.attempts.some(a => attemptSessionId(a) === d.id && attemptMemberIds(a).some(id => studentsForCohort(activeClass, activeCohort).some(s => s.id === id))));
  const pinnedVersion = digitalSession ? digitalLibrary.data?.state.versions.find(v => v.id === digitalSession.activityId && v.version === digitalSession.activityVersion) : libraryVersion;
  const digitalContent = useMemo(() => pinnedVersion ? normalizeActivity(pinnedVersion) : null, [pinnedVersion]);
  const usesDigitalInputs = !!digitalContent?.capabilities.hasStudentInputs && digitalContent.capabilities.requiresStudentSubmission;
  const digitalAccess = digitalSession ? sessionAccess(digitalSession, new Date(sessionClock).toISOString()) : undefined;
  useEffect(() => {
    if (!digitalSession || !digitalLibrary.data) return;
    const attendance = Object.assign({}, ...digitalLibrary.data.state.attempts.filter(a => attemptSessionId(a) === digitalSession.id).map(a => a.attendance ?? {}));
    const participation = digitalSession.participation ?? {};
    setStudents(previous => {
      let changed = false;
      const next = previous.map(student => {
        const observed = participation[student.id];
        const absent = observed?.level === "ABS" || attendance[student.id]?.absent || false;
        const level = observed && observed.level !== "ABS" ? observed.level : student.participation;
        if (student.absent === absent && student.participation === level) return student;
        changed = true;
        return {...student, absent, participation: level};
      });
      return changed ? next : previous;
    });
  }, [digitalLibrary.data, digitalSession?.id, digitalSession?.revision]);
  useEffect(() => { if (view === "live") void digitalLibrary.refresh(); }, [view, activityId, activeClass.id]);
  useEffect(() => {
    if (currentSession?.digitalSessionId && digitalSession?.access === "ended") finishSession();
  }, [currentSession?.digitalSessionId, digitalSession?.access]);
  const activityCompetencies = selectedActivity
    ? orderCatalogForLevel(catalog, selectedActivity.schoolLevel).filter((item) => selectedActivity.competencyIds.includes(item.id))
    : [];

  function containerOf(id: string): ContainerId | undefined {
    return (Object.keys(locations) as ContainerId[]).find((container) => locations[container].includes(id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeItemId = String(active.id);
    const source = containerOf(activeItemId);
    let destination: ContainerId | undefined;
    let overId = String(over.id);
    if (activeItemId.startsWith("group:")) {
      if (groups.some((group) => group.id === overId)) overId = `group:${overId}`;
      destination = containerOf(overId) === "room" ? "room" : undefined;
    } else if (groups.some((group) => group.id === overId)) destination = overId;
    else if (overId.startsWith("group:") && groups.some((group) => group.id === overId.slice("group:".length))) {
      destination = overId.slice("group:".length);
    } else destination = containerOf(overId);
    if (!source || !destination) return;
    if ((activeItemId.startsWith("space-") || activeItemId.startsWith("group:")) && destination !== "room") return;

    setLocations((current) => {
      if (source === destination) {
        const oldIndex = current[source].indexOf(activeItemId);
        const newIndex = overId === destination ? current[source].length - 1 : current[source].indexOf(overId);
        if (source === "room") {
          if (oldIndex < 0 || newIndex < 0) return current;
          const room = [...current.room];
          [room[oldIndex], room[newIndex]] = [room[newIndex], room[oldIndex]];
          return { ...current, room };
        }
        return { ...current, [source]: arrayMove(current[source], oldIndex, Math.max(0, newIndex)) };
      }
      const nextSource = [...current[source]];
      const insertAt = overId === destination ? current[destination].length : current[destination].indexOf(overId);
      const nextDestination = [...current[destination]];
      if (source === "room") {
        const sourceIndex = nextSource.indexOf(activeItemId);
        nextSource[sourceIndex] = newSpaceId();
        nextDestination.splice(Math.max(0, insertAt), 0, activeItemId);
      } else if (destination === "room") {
        if (insertAt < 0 || !nextDestination[insertAt]?.startsWith("space-")) return current;
        nextSource.splice(nextSource.indexOf(activeItemId), 1);
        nextDestination[insertAt] = activeItemId;
      }
      return { ...current, [source]: nextSource, [destination]: nextDestination };
    });
  }

  function updateStudents(ids: string[], updater: (student: Student) => Student) {
    setStudents((current) => current.map((student) => (ids.includes(student.id) ? updater(student) : student)));
  }

  async function cycleParticipation(id: string) {
    if (!currentSession || attendanceSaving.current) return;
    const before = studentById[id];
    if (!before) return;
    const next = ((student: Student): Student => {
      if (student.participation === "B" && !student.absent) return { ...student, participation: "A" };
      if (student.participation === "A" && !student.absent) return { ...student, absent: true };
      if (student.absent) return { ...student, absent: false, participation: "D" };
      if (student.participation === "D") return { ...student, participation: "C" };
      return { ...student, participation: "B" };
    })(before);
    attendanceSaving.current = true;
    setLaunchError("");
    try {
      if (digitalSession) {
        if (before.absent !== next.absent) {
          const copy = digitalLibrary.data?.state.attempts.find(a => attemptSessionId(a) === digitalSession.id && attemptMemberIds(a).includes(id));
          await digitalLibrary.execute({type: "session_attendance", sessionId: digitalSession.id, studentId: id,
            absent: next.absent, expectedAbsent: copy?.attendance?.[id]?.absent ?? false});
        }
        await digitalLibrary.execute({
          type: "session_participation",
          sessionId: digitalSession.id,
          studentId: id,
          level: next.absent ? "ABS" : next.participation,
          expectedRevision: digitalSession.revision,
        });
      } else if (before.absent !== next.absent && selectedActivity) {
        const {results, dates, evaluations, automatic: auto} = sessionAttendance(
          selectedActivity.competencyIds.map(cid => resultKey(selectedActivity.id, cid)), next.absent,
          {results: before.results, dates: before.resultUpdatedAt, evaluations: currentSession.evaluations?.[id], automatic: currentSession.attendanceAuto?.[id]}, new Date().toISOString());
        next.results = results; next.resultUpdatedAt = dates;
        setCurrentSession(s => s ? {...s, evaluations: {...s.evaluations, [id]: evaluations}, attendanceAuto: {...s.attendanceAuto, [id]: auto}} : s);
      }
      updateStudents([id], () => next);
    } catch (e) {
      setLaunchError(e instanceof Error ? e.message : "Participation non enregistrée.");
    } finally {
      attendanceSaving.current = false;
    }
  }

  async function setLevel(competencyId: string, level: CheckpointLevel) {
    if (!selectedActivity || !currentSession) return;
    const evaluatedIds = selectedIds.filter((id) => studentById[id] && (!studentById[id].absent || level === "ABS" || level === "NE"));
    if (!evaluatedIds.length) return;

    if (digitalSession && digitalContent?.capabilities.supportsDirectEvaluation && digitalLibrary.data && pinnedVersion) {
      const cp = pinnedVersion.checkpoints.find(checkpoint => checkpoint.competencyId === competencyId);
      if (!cp) return;
      setLaunchError("");
      try {
        let state = digitalLibrary.data.state;
        const attemptIds = [...new Set(evaluatedIds.flatMap(id => {
          const attempt = state.attempts.find(a => attemptSessionId(a) === digitalSession.id && attemptMemberIds(a).includes(id));
          return attempt ? [attempt.id] : [];
        }))];
        for (const attemptId of attemptIds) {
          const attempt = state.attempts.find(a => a.id === attemptId);
          if (!attempt) continue;
          const result = await digitalLibrary.execute({
            type: "validate_checkpoint",
            attemptId,
            expectedRevision: attempt.revision,
            checkpointId: cp.id,
            level,
          });
          state = result.state;
        }
      } catch (e) {
        setLaunchError(e instanceof Error ? e.message : "Validation impossible.");
        return;
      }
    } else if (usesDigitalInputs) {
      return;
    }

    const key = resultKey(selectedActivity.id, competencyId);
    const updatedAt = new Date().toISOString();
    updateStudents(selectedIds, (student) =>
      student.absent && level !== "ABS" && level !== "NE" ? student : {
        ...student,
        results: { ...student.results, [key]: level },
        resultUpdatedAt: { ...student.resultUpdatedAt, [key]: updatedAt },
      },
    );
    setCurrentSession((session) => {
      if (!session) return session;
      const evaluations = { ...(session.evaluations ?? {}) };
      evaluatedIds.forEach((studentId) => {
        evaluations[studentId] = { ...(evaluations[studentId] ?? {}), [key]: level };
      });
      const attendanceAuto = structuredClone(session.attendanceAuto ?? {});
      evaluatedIds.forEach(id => {if (attendanceAuto[id]) delete attendanceAuto[id][key];});
      return { ...session, activityId: selectedActivity.id, evaluations, attendanceAuto };
    });
  }

  function saveCompetency(competency: CatalogCompetency) {
    setCatalog((current) => current.some((item) => item.id === competency.id)
      ? current.map((item) => item.id === competency.id ? competency : item)
      : [...current, competency]);
  }

  function deleteCompetency(id: string) {
    const removedIds = new Set([id, ...catalog.filter((item) => item.parentId === id).map((item) => item.id)]);
    setCatalog((current) => current.filter((item) => !removedIds.has(item.id)));
    setActivities((current) => current.map((item) => ({ ...item, competencyIds: item.competencyIds.filter((competencyId) => !removedIds.has(competencyId)) })));
  }

  function reorderCompetency(activeId: string, overId: string) {
    setCatalog((current) => {
      const active = current.find((item) => item.id === activeId);
      const over = current.find((item) => item.id === overId);
      if (!active || !over || active.schoolLevel !== over.schoolLevel || active.parentId !== over.parentId) return current;
      const siblings = current
        .filter((item) => item.schoolLevel === active.schoolLevel && item.parentId === active.parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const oldIndex = siblings.findIndex((item) => item.id === activeId);
      const newIndex = siblings.findIndex((item) => item.id === overId);
      if (oldIndex < 0 || newIndex < 0) return current;
      const reordered = arrayMove(siblings, oldIndex, newIndex);
      const order = new Map(reordered.map((item, index) => [item.id, index]));
      return current.map((item) => order.has(item.id) ? { ...item, sortOrder: order.get(item.id)! } : item);
    });
  }

  function saveActivity(activity: ActivityDefinition) {
    setActivities((current) => current.some((item) => item.id === activity.id)
      ? current.map((item) => item.id === activity.id ? activity : item)
      : [...current, activity]);
    setActivityId(activity.id);
    setCurrentSession((current) => current ? { ...current, activityId: activity.id } : current);
  }

  function toggleTeacherLevel(level: SchoolLevel) {
    setTeacherLevels((current) => current.includes(level)
      ? current.length === 1 ? current : current.filter((item) => item !== level)
      : defaultTeacherLevels.filter((item) => [...current, level].includes(item)));
  }

  function deleteActivity(id: string) {
    setActivities((current) => current.filter((item) => item.id !== id));
    if (activityId === id) setActivityId("");
  }

  function readPlan(schoolClass: SchoolClass, roomId: string, cohort: Cohort) {
    const saved = localStorage.getItem(planStorageKey(schoolClass.id, roomId, cohort));
    const plan = saved ? JSON.parse(saved) : freshPlan(schoolClass, cohort);
    return { locations: { ...plan.locations, room: normalizeRoomSlots(plan.locations.room ?? [], 40) }, groups: plan.groups ?? [] };
  }

  function openClass(schoolClass: SchoolClass, roomId = activeRoomId, cohort = activeCohort) {
    if (currentSession && schoolClass.id !== currentSession.classId) {
      setView("live");
      return;
    }
    const allowedRoomIds = schoolClass.roomIds ?? rooms.map((room) => room.id);
    const resolvedRoomId = allowedRoomIds.includes(roomId) ? roomId : allowedRoomIds[0] ?? rooms[0]?.id ?? defaultRooms[0].id;
    const savedStudents = localStorage.getItem(`competenceo-students-v1:${schoolClass.id}`);
    const legacyClassroom = localStorage.getItem(`competenceo-classroom-${schoolClass.id}`);
    const legacyStudents = legacyClassroom ? JSON.parse(legacyClassroom).students : null;
    const nextStudents = savedStudents ? JSON.parse(savedStudents) : legacyStudents ?? schoolClass.students.map(studentFromSeed);
    const plan = readPlan(schoolClass, resolvedRoomId, cohort);
    setStudents(nextStudents);
    setLocations(plan.locations);
    setGroups(plan.groups);
    setActiveClassId(schoolClass.id);
    setActiveRoomId(resolvedRoomId);
    setActiveCohort(cohort);
    setSelected({ type: "student", id: plan.locations.room.find((id: string) => !id.startsWith("space-") && !id.startsWith("group:")) ?? nextStudents[0]?.id ?? "" });
    setEvaluationOpen(false);
    setEditingPlan(false);
    setView("live");
  }

  function switchPlan(roomId: string, cohort: Cohort) {
    if (currentSession) return;
    const plan = readPlan(activeClass, roomId, cohort);
    setLocations(plan.locations);
    setGroups(plan.groups);
    setActiveRoomId(roomId);
    setActiveCohort(cohort);
    setSelected({ type: "student", id: plan.locations.room.find((id: string) => !id.startsWith("space-") && !id.startsWith("group:")) ?? "" });
    setEvaluationOpen(false);
    setEditingPlan(false);
  }

  function saveClass(schoolClass: SchoolClass) {
    setClasses((current) => current.some((item) => item.id === schoolClass.id)
      ? current.map((item) => item.id === schoolClass.id ? schoolClass : item)
      : [...current, schoolClass]);
    openClass(schoolClass, activeRoomId, "all");
  }

  function updateClass(schoolClass: SchoolClass) {
    const previousClass = classes.find((item) => item.id === schoolClass.id);
    const removedStudentIds = previousClass?.students.filter((student) => !schoolClass.students.some((item) => item.id === student.id)).map((student) => student.id) ?? [];
    setClasses((current) => current.map((item) => item.id === schoolClass.id ? schoolClass : item));
    const storedStudents = schoolClass.id === activeClassId
      ? students
      : JSON.parse(localStorage.getItem(`competenceo-students-v1:${schoolClass.id}`) ?? "[]") as Student[];
    const storedById = new Map(storedStudents.map((student) => [student.id, student]));
    const nextStudents = schoolClass.students.map((seed, index) => {
      const stored = storedById.get(seed.id);
      return stored ? { ...stored, ...seed } : studentFromSeed(seed, index);
    });
    localStorage.setItem(`competenceo-students-v1:${schoolClass.id}`, JSON.stringify(nextStudents));
    if (removedStudentIds.length > 0) {
      const removed = new Set(removedStudentIds);
      setSessions((current) => current.map((session) => {
        if (session.classId !== schoolClass.id) return session;
        const participation = Object.fromEntries(Object.entries(session.participation ?? {}).filter(([studentId]) => !removed.has(studentId)));
        const evaluations = Object.fromEntries(Object.entries(session.evaluations ?? {}).filter(([studentId]) => !removed.has(studentId)));
        return { ...session, participation, evaluations, absentIds: (session.absentIds ?? []).filter((studentId) => !removed.has(studentId)) };
      }));
    }
    removedStudentIds.forEach((studentId) => {
      void fetch(`/api/teacher/classes/${encodeURIComponent(schoolClass.id)}/students/${encodeURIComponent(studentId)}`, { method: "DELETE", credentials: "include" });
    });

    const currentPlanKey = planStorageKey(activeClassId, activeRoomId, activeCohort);
    rooms.forEach((room) => (["all", "A", "B"] as Cohort[]).forEach((cohort) => {
      const key = planStorageKey(schoolClass.id, room.id, cohort);
      const saved = key === currentPlanKey
        ? { locations, groups }
        : (() => { try { return JSON.parse(localStorage.getItem(key) ?? "null"); } catch { return null; } })();
      if (saved) localStorage.setItem(key, JSON.stringify(reconcilePlan(schoolClass, cohort, saved)));
    }));

    if (schoolClass.id !== activeClassId) return;
    setStudents(nextStudents);
    const allowedRoomIds = schoolClass.roomIds ?? rooms.map((room) => room.id);
    const nextRoomId = allowedRoomIds.includes(activeRoomId) ? activeRoomId : allowedRoomIds[0] ?? rooms[0]?.id;
    const cohortHasStudents = studentsForCohort(schoolClass, activeCohort).length > 0;
    const nextCohort = activeCohort === "all" || cohortHasStudents ? activeCohort : "all";
    if (!nextRoomId) return;
    const plan = readPlan(schoolClass, nextRoomId, nextCohort);
    const reconciled = reconcilePlan(schoolClass, nextCohort, plan);
    setLocations(reconciled.locations);
    setGroups(reconciled.groups);
    setActiveRoomId(nextRoomId);
    setActiveCohort(nextCohort);
    if (selected.type === "student" && !nextStudents.some((student) => student.id === selected.id)) {
      setSelected({ type: "student", id: reconciled.locations.room.find((id) => !id.startsWith("space-") && !id.startsWith("group:")) ?? nextStudents[0]?.id ?? "" });
      setEvaluationOpen(false);
    }
  }

  function deleteClass(id: string) {
    if (classes.length === 1) return;
    const schoolClass = classes.find((item) => item.id === id);
    if (!schoolClass) return;
    const relatedSessions = sessions.filter((session) => session.classId === id).length;
    const warning = relatedSessions > 0
      ? `Supprimer « ${schoolClass.name} » ?\n\nSes élèves, ses plans et ses ${relatedSessions} séance${relatedSessions > 1 ? "s" : ""} seront définitivement supprimés. Les activités préparées seront conservées.`
      : `Supprimer « ${schoolClass.name} » ?\n\nSes élèves et ses plans seront définitivement supprimés. Les activités préparées seront conservées.`;
    if (!window.confirm(warning)) return;
    const fallbackClass = classes.find((item) => item.id !== id);
    setClasses((current) => current.filter((schoolClass) => schoolClass.id !== id));
    setSessions((current) => current.filter((session) => session.classId !== id));
    localStorage.removeItem(`competenceo-classroom-${id}`);
    localStorage.removeItem(`competenceo-students-v1:${id}`);
    rooms.forEach((room) => (["all", "A", "B"] as Cohort[]).forEach((cohort) => localStorage.removeItem(planStorageKey(id, room.id, cohort))));
    void fetch(`/api/teacher/classes/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
    if (id === activeClassId && fallbackClass) openClass(fallbackClass, activeRoomId, "all");
  }

  function addRoom(name: string) {
    const id = `room-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setRooms((current) => [...current, { id, name }]);
  }

  function deleteRoom(id: string) {
    if (rooms.length === 1) return;
    const roomToDelete = rooms.find((room) => room.id === id);
    if (!roomToDelete || !window.confirm(`Supprimer la salle « ${roomToDelete.name} » ?\n\nTous les plans enregistrés dans cette salle seront définitivement supprimés.`)) return;
    const fallbackRoom = rooms.find((room) => room.id !== id);
    if (!fallbackRoom) return;
    setRooms((current) => current.filter((room) => room.id !== id));
    setClasses((current) => current.map((schoolClass) => {
      if (!schoolClass.roomIds) return schoolClass;
      const roomIds = schoolClass.roomIds.filter((roomId) => roomId !== id);
      return { ...schoolClass, roomIds: roomIds.length > 0 ? roomIds : [fallbackRoom.id] };
    }));
    classes.forEach((schoolClass) => (["all", "A", "B"] as Cohort[]).forEach((cohort) => localStorage.removeItem(planStorageKey(schoolClass.id, id, cohort))));
    if (id === activeRoomId) switchPlan(fallbackRoom.id, activeCohort);
  }

  function resetDemo() {
    if (currentSession) return;
    localStorage.removeItem("competenceo-demo-students");
    localStorage.removeItem("competenceo-demo-plan");
    localStorage.removeItem("competenceo-demo-groups");
    localStorage.removeItem(planStorageKey(activeClassId, activeRoomId, activeCohort));
    const plan = freshPlan(activeClass, activeCohort);
    setLocations(plan.locations);
    setGroups(plan.groups);
    setSelected({ type: "student", id: plan.locations.room.find((id) => !id.startsWith("space-")) ?? "" });
    setEvaluationOpen(false);
  }

  function addGroup() {
    const emptyIndex = locations.room.findIndex((item) => item.startsWith("space-"));
    if (emptyIndex < 0) return;
    const number = groups.length + 1;
    const id = `group-${Date.now()}`;
    setGroups((current) => [...current, { id, name: `Sous-groupe ${number}` }]);
    setLocations((current) => {
      const room = [...current.room];
      room[emptyIndex] = `group:${id}`;
      return { ...current, room, [id]: [] };
    });
    setSelected({ type: "group", id });
    setEvaluationOpen(false);
  }

  function startSession(digitalSessionId?: string) {
    if (currentSession) return;
    const startedAt = new Date().toISOString();
    const cohortIds = new Set(studentsForCohort(activeClass, activeCohort).map((student) => student.id));
    setStudents((current) => current.map((student) => cohortIds.has(student.id) ? { ...student, participation: "B", absent: digitalSessionId
      ? digitalLibrary.data?.state.attempts.find(a => attemptSessionId(a) === digitalSessionId && attemptMemberIds(a).includes(student.id))?.attendance?.[student.id]?.absent ?? false : false } : student));
    setCurrentSession({
      ...(digitalSessionId ? {digitalSessionId} : {}),
      id: `session-${Date.now()}`,
      classId: activeClass.id,
      roomId: activeRoom.id,
      cohort: activeCohort,
      activityId: selectedActivity?.id ?? "",
      startedAt,
      period: activePeriod,
    });
  }

  async function startActivity() {
    if (digitalLibrary.busy) return;
    setLaunchError("");
    try {
      if (selectedActivity && !digitalContent) throw new Error("Recharge la bibliothèque avant de démarrer cette activité.");
      if (digitalSession) { await changeDigitalAccess(digitalSession, "resume"); return; }
      if (!libraryVersion) { startSession(); return; }
      const memberIds = studentsForCohort(activeClass, activeCohort).map(s => s.id);
      const sessionId = crypto.randomUUID();
      await digitalLibrary.execute({type: "launch_session", sessionId, classId: activeClass.id,
        activityId: libraryVersion.id, expectedVersion: libraryVersion.version, memberIds,
        groups: usesDigitalInputs
          ? groups.map(g => ({id: g.id, name: g.name, memberIds: (locations[g.id] ?? []).filter(id => memberIds.includes(id))})).filter(g => g.memberIds.length > 0)
          : []});
      startSession(sessionId);
      setTracking(usesDigitalInputs);
    } catch (e) { setLaunchError(e instanceof Error ? e.message : "Démarrage impossible."); }
  }

  async function changeDigitalAccess(session: DigitalSession, action: "pause" | "resume" | "finish") {
    setLaunchError("");
    try {
      await digitalLibrary.execute({type: "session_access", sessionId: session.id, expectedRevision: session.revision, action});
      if (action === "resume") { startSession(session.id); setTracking(true); }
      else finishSession();
    } catch (e) { setLaunchError(e instanceof Error ? e.message : "Changement impossible."); }
  }

  function finishActivity() {
    if (currentSession?.digitalSessionId && !digitalSession) {
      setLaunchError("Recharge les séances avant de terminer l’activité numérique."); return;
    }
    if (digitalSession) void changeDigitalAccess(digitalSession, "finish");
    else finishSession();
  }

  function finishSession() {
    if (!currentSession) return;
    const cohortIds = new Set(studentsForCohort(activeClass, currentSession.cohort).map((student) => student.id));
    const participation = Object.fromEntries(students.filter((student) => cohortIds.has(student.id)).map((student) => [student.id, student.participation]));
    const absentIds = students.filter((student) => cohortIds.has(student.id) && student.absent).map((student) => student.id);
    const finished = { ...currentSession, activityId: selectedActivity?.id ?? currentSession.activityId ?? "", endedAt: new Date().toISOString(), participation, absentIds };
    setSessions((current) => current.some(s => s.id === finished.id) ? current : [finished, ...current]);
    setCurrentSession(null);
  }

  function reconcileSessionResults(previousSessions: SessionRecord[], nextSessions: SessionRecord[]) {
    const tracked = new Map<string, Set<string>>();
    previousSessions.filter((session) => session.classId === activeClass.id).forEach((session) => {
      Object.entries(session.evaluations ?? {}).forEach(([studentId, values]) => {
        const keys = tracked.get(studentId) ?? new Set<string>();
        Object.keys(values).forEach((key) => keys.add(key));
        tracked.set(studentId, keys);
      });
    });
    const rebuilt = new Map<string, { results: Record<string, CheckpointLevel>; dates: Record<string, string> }>();
    [...nextSessions].filter((session) => session.classId === activeClass.id).sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt)).forEach((session) => {
      Object.entries(session.evaluations ?? {}).forEach(([studentId, values]) => {
        const current = rebuilt.get(studentId) ?? { results: {}, dates: {} };
        Object.entries(values).forEach(([key, level]) => {
          current.results[key] = level;
          current.dates[key] = session.endedAt ?? session.startedAt;
        });
        rebuilt.set(studentId, current);
      });
    });
    setStudents((current) => current.map((student) => {
      const results = { ...student.results };
      const resultUpdatedAt = { ...(student.resultUpdatedAt ?? {}) };
      tracked.get(student.id)?.forEach((key) => { delete results[key]; delete resultUpdatedAt[key]; });
      const replacement = rebuilt.get(student.id);
      return replacement ? { ...student, results: { ...results, ...replacement.results }, resultUpdatedAt: { ...resultUpdatedAt, ...replacement.dates } } : { ...student, results, resultUpdatedAt };
    }));
  }

  function updateHistorySession(updated: HistorySession) {
    const next = sessions.map((session) => session.id === updated.id ? updated as SessionRecord : session);
    reconcileSessionResults(sessions, next);
    setSessions(next);
  }

  function deleteHistorySession(deleted: HistorySession) {
    const next = sessions.filter((session) => session.id !== deleted.id);
    reconcileSessionResults(sessions, next);
    setSessions(next);
  }

  function deleteGroup(id: string) {
    setLocations((current) => {
      const { [id]: members = [], ...rest } = current;
      const marker = `group:${id}`;
      const markerIndex = rest.room.indexOf(marker);
      const room = [...rest.room];
      if (markerIndex >= 0) room[markerIndex] = members.shift() ?? newSpaceId();
      members.forEach((member) => {
        const emptyIndex = room.findIndex((item) => item.startsWith("space-"));
        if (emptyIndex >= 0) room[emptyIndex] = member;
      });
      return { ...rest, room };
    });
    setGroups((current) => current.filter((group) => group.id !== id));
    setSelected({ type: "student", id: locations[id]?.[0] ?? locations.room.find((item) => !item.startsWith("space-")) ?? "s1" });
    setEvaluationOpen(false);
  }

  const card = (id: string, inRoom = false) => (
    <SortableStudentCard
      key={id}
      student={studentById[id]}
      activityId={activityId}
      competencyIds={activityCompetencies.map((item) => item.id)}
      selected={selected.type === "student" && selected.id === id}
      inGroup={!inRoom}
      editing={false}
      sessionActive={Boolean(currentSession)}
      onSelect={() => { setSelected({ type: "student", id }); setEvaluationOpen(true); }}
      onCycleParticipation={() => cycleParticipation(id)}
    />
  );

  const roomItem = (id: string) => {
    if (id.startsWith("space-")) return <SortableSpace key={id} id={id} />;
    if (id.startsWith("group:")) {
      const groupId = id.slice("group:".length);
      const group = groups.find((item) => item.id === groupId);
      if (!group) return null;
      const groupStudents = (locations[group.id] ?? []).map((studentId) => studentById[studentId]).filter(Boolean);
      const presentStudents = groupStudents.filter((student) => !student.absent);
      const groupStatuses = presentStudents.map((student) => statusFor(student, activityId, activityCompetencies.map((item) => item.id)));
      const groupStatus: StudentStatus = groupStatuses.length === 0 || groupStatuses.every((status) => status === "todo")
        ? "todo"
        : groupStatuses.every((status) => status === "done")
          ? "done"
          : "progress";
      return (
        <SortableContext key={group.id} items={locations[group.id] ?? []} strategy={rectSortingStrategy}>
          <LabGroup
            id={group.id}
            markerId={id}
            name={group.name}
            shortName={`G${groups.findIndex((item) => item.id === group.id) + 1}`}
            memberCount={(locations[group.id] ?? []).length}
            status={groupStatus}
            selected={selected.type === "group" && selected.id === group.id}
            editing={editingPlan}
            onSelect={() => { setSelected({ type: "group", id: group.id }); setEvaluationOpen(true); }}
            onDelete={() => deleteGroup(group.id)}
          >
            {(locations[group.id] ?? []).map((studentId) => card(studentId))}
            {(locations[group.id] ?? []).length === 0 && <div className="group-empty">Dépose un élève ici</div>}
          </LabGroup>
        </SortableContext>
      );
    }
    return card(id, true);
  };

  return (
    <div className={`app-shell ${sidebarExpanded ? "sidebar-expanded" : ""}`}>
      <aside className={`side-navigation ${sidebarExpanded ? "expanded" : "collapsed"}`}>
        <div className="side-brand">
          <img className="brand-logo" src="/competenceo-logo-192.png" alt="CompetenceO" />
          {sidebarExpanded && <strong>CompetenceO</strong>}
          <button className="sidebar-toggle" onClick={() => setSidebarExpanded((value) => !value)} aria-label={sidebarExpanded ? "Réduire le menu" : "Ouvrir le menu"} title={sidebarExpanded ? "Réduire le menu" : "Ouvrir le menu"}><Menu size={21} /></button>
        </div>
        <nav className="side-menu" aria-label="Navigation principale">
          <button className={view==="live"?"active":""} onClick={()=>setView("live")} title="Séance"><Users size={20}/><span>Séance</span></button>
          <button className={view==="activities"?"active":""} onClick={()=>setView("activities")} title="Bibliothèque"><BookOpen size={20}/><span>Bibliothèque</span></button>
          <a href="/animations" target="_blank" rel="noopener noreferrer" title="Animations (nouvel onglet)"><Play size={20}/><span>Animations</span></a>
          <button className={view==="written"?"active":""} onClick={()=>setView("written")}><BookOpen size={20}/><span>Devoirs écrits</span></button>
          <button className={view==="board"?"active":""} onClick={()=>setView("board")} title="Tableau de classe"><LayoutGrid size={20}/><span>Tableau de classe</span></button>
          <button className={view==="corrections"?"active":""} onClick={()=>setView("corrections")} title="Corrections"><ListChecks size={20}/><span>Corrections</span></button>
          <button className={view==="classes"||view==="student"?"active":""} onClick={()=>setView("classes")} title="Élèves"><Users size={20}/><span>Élèves</span></button>
          <button className={view==="competencies"?"active":""} onClick={()=>setView("competencies")} title="Compétences"><BarChart3 size={20}/><span>Compétences</span></button>
          <button className={view==="history"?"active":""} onClick={()=>setView("history")} title="Historique"><History size={20}/><span>Historique</span></button>
          <div className="side-menu-separator" />
          <button className={["settings","rooms","maintenance","pronote","admin"].includes(view)?"active":""} onClick={()=>setView("settings")} title="Paramètres"><Settings2 size={20}/><span>Paramètres</span></button>
          <button className={view==="account"?"active":""} onClick={()=>setView("account")} title="Mon compte"><KeyRound size={20}/><span>Mon compte</span></button>
        </nav>
        <button className="sidebar-logout" onClick={onLogout} title={`Se déconnecter · ${currentUser.email ?? currentUser.fullName}`}><LogOut size={20}/><span>Se déconnecter</span></button>
      </aside>
      <header className="topbar">
        {view === "live" || view === "history" ? <div className="session-title">
          <label className="session-select class-select"><span>Classe</span><select value={activeClass.id} disabled={Boolean(currentSession)} onChange={(event) => { const schoolClass = classes.find((item) => item.id === event.target.value); if (schoolClass) openClass(schoolClass, activeRoomId, "all"); }}>{classes.map((schoolClass) => <option key={schoolClass.id} value={schoolClass.id}>{schoolClass.name}</option>)}</select><ChevronDown size={15} /></label>
          {hasOfficialGroups && <label className="session-select"><span>Effectif</span><select value={activeCohort} disabled={Boolean(currentSession)} onChange={(event) => switchPlan(activeRoomId, event.target.value as Cohort)}><option value="all">Classe entière</option>{groupACount > 0 && <option value="A">Groupe A ({groupACount})</option>}{groupBCount > 0 && <option value="B">Groupe B ({groupBCount})</option>}</select><ChevronDown size={15} /></label>}
          {availableRooms.length > 1 && <label className="session-select"><span>Salle</span><select value={activeRoom.id} disabled={Boolean(currentSession)} onChange={(event) => switchPlan(event.target.value, activeCohort)}>{availableRooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}</select><ChevronDown size={15} /></label>}
          <label className="session-select period-select"><span>Période</span><select value={activePeriod} disabled={Boolean(currentSession)} onChange={(event) => setActivePeriod(Number(event.target.value) as Period)}><option value={1}>Période 1</option><option value={2}>Période 2</option><option value={3}>Période 3</option></select><ChevronDown size={15} /></label>
          <ActivityPicker
            disabled={!!currentSession?.digitalSessionId}
            activities={availableSessionActivities}
            schoolLevel={activeClass.schoolLevel}
            classId={activeClass.id}
            selectedId={selectedActivity?.id ?? ""}
            currentSessionActivityId={currentSession?.activityId}
            sessions={sessions}
            onSelect={(nextActivityId) => {
              if (currentSession?.digitalSessionId) return;
              if (currentSession && nextActivityId !== currentSession.activityId) {
                finishSession();
              }
              setActivityId(nextActivityId);
            }}
          />
        </div>
        : <div className="teacher-section-title">{({board:"Tableau de classe",written:"Devoirs écrits",activities:"Bibliothèque",corrections:"Corrections",classes:"Élèves",competencies:"Compétences",settings:"Paramètres",rooms:"Salles",maintenance:"Maintenance",account:"Mon compte"} as Record<string,string>)[view]??"CompetenceO"}{currentSession&&<button onClick={()=>setView("live")}>Retour à la séance en cours</button>}</div>}
        {view === "live" && !(tracking && digitalSession) && <div className="topbar-live-actions">
          <button className={currentSession ? "session-stop" : "session-start"} disabled={digitalLibrary.busy} onClick={currentSession ? finishActivity : () => void startActivity()}>{currentSession ? <Square size={15} /> : <Play size={16} />}{currentSession ? (digitalSession ? "Terminer l’activité" : "Terminer") : digitalSession ? "Reprendre l’activité" : "Démarrer"}</button>
          {digitalSession && <>
            <button className="session-access-action" onClick={()=>{setTracking(true);void digitalLibrary.refresh();}}>Suivi en direct</button>
            <span className="session-access-status" role="status">{digitalAccess === "active" ? "Activité accessible · fenêtre de 3 h" : "Activité en pause · réponses conservées"}</span>
            {(digitalAccess === "active" || currentSession) && <button className="session-access-action" disabled={digitalLibrary.busy} onClick={() => void changeDigitalAccess(digitalSession, digitalAccess === "active" ? "pause" : "resume")}>{digitalAccess === "active" ? "Pause" : "Reprendre"}</button>}
            {!currentSession && <button className="session-access-action" disabled={digitalLibrary.busy} onClick={() => void changeDigitalAccess(digitalSession, "finish")}>Terminer l’activité</button>}
          </>}
          {editingPlan && <button className="topbar-tool" onClick={addGroup} title="Ajouter un sous-groupe"><Users size={16} /><span>Sous-groupe</span></button>}
          <button className={`topbar-tool ${editingPlan ? "editing" : ""}`} onClick={() => setEditingPlan((value) => !value)} title={editingPlan ? "Enregistrer le plan" : "Modifier le plan"}>{editingPlan ? <Save size={16} /> : <LayoutGrid size={16} />}<span>{editingPlan ? "Enregistrer" : "Plan"}</span></button>
          <button className="topbar-tool icon-only" disabled={Boolean(currentSession)} onClick={resetDemo} title="Réinitialiser le plan" aria-label="Réinitialiser le plan"><Redo2 size={16} /></button>
        </div>}
        <button className={`sync-state ${syncStatus}`} disabled={syncStatus === "syncing" || syncStatus === "offline"} onClick={() => void synchronizeNow()} title={syncStatus === "error" ? "Réessayer la synchronisation" : "Synchroniser maintenant"}>
          {syncStatus === "offline" || syncStatus === "error" ? <CloudOff size={17} /> : <Cloud size={17} />}
          <span>{syncStatus === "offline" ? "Enregistré sur cet appareil" : syncStatus === "pending" ? "Sauvegarde en attente" : syncStatus === "syncing" ? "Synchronisation…" : syncStatus === "error" ? "Sauvegarde à relancer" : "Synchronisé"}</span>
        </button>
      </header>

      {(launchError || digitalLibrary.error) && <p role="alert">{launchError || digitalLibrary.error} <button onClick={() => void digitalLibrary.refresh()}>Actualiser les séances</button></p>}
      {(view==="live"||view==="history")&&<nav className="teacher-subnav" aria-label="Séance"><button onClick={()=>setView("live")} aria-current={view==="live"?"page":undefined}>Séance en cours</button><button onClick={()=>setView("history")} aria-current={view==="history"?"page":undefined}>Historique des séances</button></nav>}
      {["settings","rooms","maintenance","pronote","admin"].includes(view)&&<nav className="teacher-subnav" aria-label="Paramètres"><button onClick={()=>setView("settings")}>Programmes et réglages</button><button onClick={()=>setView("rooms")}>Salles</button>{currentUser.role==="admin"&&<><button onClick={()=>setView("pronote")}>Intégrations</button><button onClick={()=>setView("admin")}>Administration</button></>}<button onClick={()=>setView("maintenance")}>Maintenance</button></nav>}
      {libraryVisited&&<div hidden={view!=="activities" || (tracking && !!digitalSession)}><Suspense fallback={<p>Chargement de la bibliothèque…</p>}><ActivityLibraryView onPublished={saveActivity} /></Suspense></div>}
      {(view==="live"||view==="activities")&&digitalSession&&digitalLibrary.data&&(tracking ?
        <SessionActivityPanel execute={digitalLibrary.execute} key={digitalSession.id} state={digitalLibrary.data.state} sessionId={digitalSession.id} classes={classes} busy={digitalLibrary.busy} onClose={()=>{setTracking(false);setView("live");}} onConsultAttempt={attemptId=>{setCorrectionTarget({activityId:digitalSession.activityId,attemptId,consultation:true,key:correctionTarget.key+1});setTracking(false);setView("corrections");}} onAccess={action=>void changeDigitalAccess(digitalSession,action)} onStop={()=>void changeDigitalAccess(digitalSession,"finish")} onUpdate={digitalLibrary.applyLive} /> :
        <button className="tracking-reopen" onClick={()=>{setTracking(true);void digitalLibrary.refresh();}}>Ouvrir le suivi de l’activité</button>)}
      {(view === "live" || view === "activities") && tracking && digitalSession ? null : view === "board" ? <Suspense fallback={<p>Chargement du tableau…</p>}><ClassBoard/></Suspense> : view === "written" ? <Suspense fallback={<p>Chargement des devoirs…</p>}><WrittenHomeworks/></Suspense> : view === "corrections" ? <CorrectionsView key={correctionTarget.key} activityId={correctionTarget.activityId} attemptId={correctionTarget.attemptId} consultation={correctionTarget.consultation} onReturn={correctionTarget.consultation?()=>{setView("live");setTracking(true);void digitalLibrary.refresh();}:undefined} onStudent={s=>{setProfileTarget(s);setView("classes");}} />
      : view === "competencies" ? <CompetenciesView classes={classes} referential={<CatalogView catalog={catalog} visibleLevels={teacherLevels} onSave={saveCompetency} onDelete={deleteCompetency} onReorder={reorderCompetency} onBack={()=>setView("competencies")} />} />
      : view === "maintenance" ? <ReferenceMaintenancePanel />
      : view === "rooms" ? <fieldset disabled={!!currentSession} className="management-guard"><ClassesView mode="rooms" classes={classes} visibleLevels={teacherLevels} activeClassId={activeClassId} rooms={rooms} activeRoomId={activeRoomId} onSave={saveClass} onUpdate={updateClass} onOpen={(id) => { if(currentSession){setView("live");return;} const schoolClass = classes.find((item) => item.id === id); if (schoolClass) openClass(schoolClass); }} onDelete={deleteClass} onAddRoom={addRoom} onOpenRoom={(id) => { switchPlan(id, activeCohort); setView("live"); }} onDeleteRoom={deleteRoom} /></fieldset>
      : view === "settings" ? (

        <SettingsHub teacherLevels={teacherLevels} allLevels={defaultTeacherLevels} activePeriod={activePeriod} onToggleLevel={toggleTeacherLevel} onPeriodChange={setActivePeriod} showPrograms={currentUser.role === "admin"} onOpenPrograms={() => { window.location.href = "/programmes"; }} />
      ) : view === "catalog" ? (
        <CatalogView catalog={catalog} visibleLevels={teacherLevels} onSave={saveCompetency} onDelete={deleteCompetency} onReorder={reorderCompetency} onBack={() => setView("settings")} />
      ) : view === "activities" ? (
        null
      ) : view === "classes" ? (
        <StudentsView key={profileTarget?.studentId??"directory"} classes={classes} initialStudent={profileTarget} management={<fieldset disabled={!!currentSession} className="management-guard"><ClassesView mode="classes" classes={classes} visibleLevels={teacherLevels} activeClassId={activeClassId} rooms={rooms} activeRoomId={activeRoomId} onSave={saveClass} onUpdate={updateClass} onOpen={(id) => { if(currentSession){setView("live");return;} const schoolClass = classes.find((item) => item.id === id); if (schoolClass) openClass(schoolClass); }} onDelete={deleteClass} onAddRoom={addRoom} onOpenRoom={(id) => { switchPlan(id, activeCohort); setView("live"); }} onDeleteRoom={deleteRoom} /></fieldset>} accounts={<StudentAccountsView classes={classes}/>} renderProfile={(c,id)=>{const stored=JSON.parse(localStorage.getItem('competenceo-students-v1:'+c.id)??'[]') as Student[];const seed=c.students.find(s=>s.id===id);const student=c.id===activeClass.id?studentById[id]:stored.find(s=>s.id===id)??(seed?studentFromSeed(seed,0):null);return student?<StudentDashboard key={c.id+id} student={student} classId={c.id} className={c.name} schoolLevel={c.schoolLevel} avatarUrl={avatarFor(student)} catalog={catalog} activities={activities} sessions={sessions} onBack={()=>setView("classes")} hideBack teacherNavigation/>:null;}} />
      ) : view === "history" ? (
        <SessionsView className={activeClass.name} sessions={sessions.filter((session) => session.classId === activeClass.id)} students={students} activities={activities} catalog={catalog} onSave={updateHistorySession} onDelete={deleteHistorySession} />
      ) : view === "accounts" ? (
        <StudentAccountsView classes={classes} />
      ) : view === "admin" && currentUser.role === "admin" ? (
        <AdminTeachersView />
      ) : view === "pronote" && currentUser.role === "admin" ? (
        <PronoteView localClasses={classes.map(({ id, name }) => ({ id, name }))} />
      ) : view === "account" ? (
        <AccountHub onLogout={onLogout} currentUser={currentUser} backupDisabled={Boolean(currentSession)} onExport={onExport} onRestore={onRestore} />
      ) : view === "student" && dashboardStudent ? (
        <div className="student-preview"><div className="student-preview-bar"><strong>Vue élève</strong><span>Tu consultes exactement ce que voit {dashboardStudent.firstName}.</span><button onClick={() => setView("live")}>Revenir à mon espace professeur</button></div><StudentDashboard student={dashboardStudent} classId={activeClass.id} className={activeClass.name} schoolLevel={activeClass.schoolLevel} avatarUrl={avatarFor(dashboardStudent)} catalog={catalog} activities={activities} sessions={sessions} onBack={() => setView("live")} hideBack /></div>
      ) : (
      <main className="workspace">
        <section className="room-panel">
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={({ active }) => setActiveId(String(active.id))}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
          >
            <div className="classroom-canvas">
              <div className="board"><span>TABLEAU</span></div>
              <SortableContext items={locations.room} strategy={rectSortingStrategy}>
                <div className="student-grid">{locations.room.map(roomItem)}</div>
              </SortableContext>
            </div>
            <DragOverlay>
              {activeId ? (
                <div className="drag-preview">
                  {studentById[activeId]?.firstName ?? (activeId.startsWith("group:") ? "Sous-groupe" : "Espace vide")}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </section>

        <aside className={`evaluation-panel ${evaluationOpen && selectedIds.length > 0 ? "open" : ""}`} aria-hidden={!evaluationOpen || selectedIds.length === 0}>
          <div className="panel-head">
            <button className="close-evaluation" onClick={() => setEvaluationOpen(false)} aria-label="Fermer le panneau d’évaluation"><X size={20} /></button>
            <span className="eyebrow">Évaluation en direct</span>
            <h2>{selected.type === "group" ? selectedGroup?.name ?? "Sous-groupe" : selectedStudent ? `${selectedStudent.firstName} ${selectedStudent.lastName}` : "Sélection"}</h2>
            <p>{selected.type === "group" ? `${selectedIds.length} élèves sélectionnés` : "1 élève sélectionné"}</p>
            {selectedStudent && <button className="open-dashboard" onClick={() => { setDashboardStudentId(selectedStudent.id); setView("student"); }}><BarChart3 size={16} /> Voir comme l’élève</button>}
          </div>

          {!currentSession && <div className="session-callout"><Play size={18} /><div><strong>Démarre la séance pour évaluer</strong><span>Tu peux choisir l’activité avant de commencer.</span></div></div>}

          {selectedStudent && (
            <div className="participation-box">
              <div><span>Participation active</span><small>B par défaut pour les présents</small></div>
              <button
                className={`big-level participation-${selectedStudent.absent ? "absent" : selectedStudent.participation.toLowerCase()}`}
                disabled={!currentSession}
                onClick={() => cycleParticipation(selectedStudent.id)}
              >
                {selectedStudent.absent ? "ABS" : selectedStudent.participation}
              </button>
            </div>
          )}

          {usesDigitalInputs ? (
            <div className="empty-activity"><strong>Production numérique</strong><p>Les réponses et checkpoints sont corrigés sur la copie commune, depuis le suivi des affectations.</p><button onClick={() => setView("activities")}>Ouvrir les travaux</button></div>
          ) : !selectedActivity ? (
            <div className="empty-activity">
              <BookOpen size={28} />
              <strong>Participation uniquement</strong>
              <p>Choisis une activité si tu veux aussi évaluer ses compétences.</p>
            </div>
          ) : (
            <div className="competency-list">
              <div className="section-title"><span>Compétences de l’activité</span><button onClick={() => setView("activities")}><Plus size={15} /> Modifier</button></div>
              {activityCompetencies.map((competency) => {
                const key = resultKey(selectedActivity.id, competency.id);
                const levels = selectedIds.map((id) => studentById[id]?.results[key]).filter(Boolean);
                const commonLevel = levels.length > 0 && levels.every((level) => level === levels[0]) ? levels[0] : undefined;
                return (
                  <div className="competency" key={competency.id}>
                    <div><strong>{competency.code}</strong><span>{competency.label}</span></div>
                    <div className="level-buttons">
                      {(["A", "B", "C", "D", "NE", "ABS"] as CheckpointLevel[]).map((level) => (
                        <button
                          key={level}
                          className={commonLevel === level ? `active level-${level.toLowerCase()}` : ""}
                          disabled={!currentSession}
                          onClick={() => void setLevel(competency.id, level)}
                          aria-label={`${competency.label} : niveau ${level}`}
                          aria-pressed={commonLevel === level}
                        >
                          {commonLevel === level && <Check size={13} />}{level === "NE" ? "Non évalué" : level === "ABS" ? "Absent" : level}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="panel-footer">
            <span><Cloud size={15} /> Sauvegarde automatique sur cet appareil</span>
            <button className={currentSession ? "primary finish-session" : "primary"} disabled={digitalLibrary.busy} onClick={currentSession ? finishActivity : () => void startActivity()}>{currentSession ? <Square size={16} /> : <Play size={17} />}{currentSession ? (digitalSession ? "Terminer l’activité" : "Terminer la séance") : digitalSession ? "Reprendre l’activité" : "Démarrer la séance"}</button>
          </div>
        </aside>
      </main>
      )}
    </div>
  );
}

export default App;

